import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, ImagePlus } from "lucide-react";
import {
  listStylePresetRefs,
  uploadStylePresetRef,
  deleteStylePresetRef,
} from "@/lib/api";

/**
 * Per-user reference images for one style preset. Rendered inline on
 * each Style Gallery card. Users upload PNG/JPG/WEBP thumbnails they
 * want gpt-image-1 to pixel-anchor against — every scene, gallery
 * preview, and thumbnail render that uses this preset picks up these
 * refs automatically.
 */
export default function PresetRefManager({ presetKey, onRefsChanged }) {
  const [refs, setRefs] = useState([]);
  const [max, setMax] = useState(4);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingId, setDeletingId] = useState("");
  const fileInputRef = useRef(null);

  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try {
      const d = await listStylePresetRefs(presetKey);
      setRefs(d?.refs || []);
      if (d?.max) setMax(d.max);
      setLoadError("");
    } catch (e) {
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.detail
        || e?.message
        || "Couldn't load references";
      // Do NOT fire a toast per preset (13 presets would stack 13
      // toasts on every Style Gallery load). Instead render a small
      // inline error banner on THIS card so the user sees WHY the
      // grid is empty instead of assuming their refs vanished.
      setLoadError(
        status === 401
          ? "Session expired — sign in again"
          : status
            ? `[${status}] ${detail}`
            : detail,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [presetKey]);

  const onPickFile = () => {
    if (uploading || refs.length >= max) return;
    fileInputRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("PNG, JPG, or WEBP only");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Max file size 8 MB");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const d = await uploadStylePresetRef(presetKey, f, {
        onProgress: setProgress,
      });
      if (d?.ref) {
        setRefs((r) => [...r, d.ref]);
        onRefsChanged?.();
        toast.success("Reference added");
      }
    } catch (err) {
      // Surface the real HTTP status + server detail so a stale session
      // (401 "Not authenticated") is obviously an auth problem instead
      // of a mysterious "Upload failed". Same treatment for 413 (file
      // too big at the ingress) and 500 (server-side failure).
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || "Upload failed";
      if (status === 401) {
        toast.error("Session expired — sign out and sign back in with Google, then retry the upload.");
      } else if (status === 413) {
        toast.error("File too large for the upload proxy (try under 5 MB or a JPG).");
      } else if (status) {
        toast.error(`Upload failed [${status}]: ${detail}`);
      } else {
        toast.error(`Upload failed: ${detail}`);
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDelete = async (refId) => {
    if (deletingId) return;
    setDeletingId(refId);
    try {
      await deleteStylePresetRef(presetKey, refId);
      setRefs((r) => r.filter((x) => x.ref_id !== refId));
      onRefsChanged?.();
      toast.success("Reference removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId("");
    }
  };

  const canUpload = !uploading && refs.length < max;
  const remaining = max - refs.length;

  return (
    <div
      data-testid={`preset-ref-manager-${presetKey}`}
      className="pt-3 border-t border-zinc-900 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">
          reference pack
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
          {loading ? "…" : `${refs.length}/${max}`}
        </div>
      </div>

      {loadError && !loading && (
        <div
          data-testid={`preset-ref-load-error-${presetKey}`}
          className="border border-red-500/40 bg-red-950/30 rounded-sm px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-red-300"
        >
          ⚠ {loadError}
        </div>
      )}

      {loading ? (
        <div className="h-14 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-zinc-700" />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5">
          {refs.map((r) => (
            <div
              key={r.ref_id}
              className="relative aspect-square bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden group"
              data-testid={`preset-ref-${presetKey}-${r.ref_id}`}
            >
              <img
                src={r.url}
                alt={r.filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => onDelete(r.ref_id)}
                disabled={deletingId === r.ref_id}
                data-testid={`preset-ref-delete-${presetKey}-${r.ref_id}`}
                className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                title="Remove reference"
              >
                {deletingId === r.ref_id ? (
                  <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                ) : (
                  <X className="w-4 h-4 text-red-400" />
                )}
              </button>
            </div>
          ))}
          {canUpload && (
            <button
              type="button"
              onClick={onPickFile}
              data-testid={`preset-ref-upload-${presetKey}`}
              className="aspect-square border border-dashed border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 text-zinc-500 rounded-sm flex flex-col items-center justify-center gap-1 transition-colors"
              title={`Upload reference (${remaining} slot${remaining === 1 ? "" : "s"} left)`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="font-mono text-[8px]">{progress}%</span>
                </>
              ) : (
                <>
                  <ImagePlus className="w-4 h-4" />
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em]">
                    add
                  </span>
                </>
              )}
            </button>
          )}
          {/* Fill remaining slots with disabled placeholders so the
              grid stays a clean 4-wide row even with 1 ref uploaded. */}
          {!canUpload && refs.length < max && Array.from({ length: max - refs.length - 1 }).map((_, i) => (
            <div
              key={`ph-${i}`}
              className="aspect-square border border-dashed border-zinc-900 rounded-sm"
            />
          ))}
        </div>
      )}

      {refs.length === 0 && !loading && !uploading && (
        <p className="text-[11px] text-zinc-500 leading-relaxed">
          <Upload className="inline w-3 h-3 mr-1 -mt-0.5" />
          Upload your own thumbnails in this style — every render on
          this preset will pixel-anchor to them.
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid={`preset-ref-file-input-${presetKey}`}
      />
    </div>
  );
}
